import React from 'react';
import { AnalysisResult } from '../types';

interface KPICardsProps {
  result: AnalysisResult;
}

export const KPICards: React.FC<KPICardsProps> = ({ result }) => {
  const { fvProfile, classification, splits } = result;

  const getBadgeColor = (label: string) => {
    switch (label) {
      case 'Speed-oriented': return 'bg-cyan-950 text-cyan-400 border border-cyan-800';
      case 'Force-oriented': return 'bg-theme-accent-soft text-theme-accent border border-theme-accent-3';
      case 'Balanced': return 'bg-green-950 text-green-400 border border-green-800';
      default: return 'bg-slate-900 text-theme-muted border border-theme-border';
    }
  };

  const cards = [
    {
      title: 'F₀ (N/kg)',
      value: fvProfile.F0_rel.toFixed(2),
      description: 'Max horizontal force capacity',
      accent: true
    },
    {
      title: 'V₀ (m/s)',
      value: fvProfile.V0.toFixed(2),
      description: 'Theoretical max velocity',
      accent: false
    },
    {
      title: 'Pmax (W/kg)',
      value: fvProfile.Pmax_rel.toFixed(1),
      description: 'Peak power output',
      accent: false
    },
    {
      title: 'R²',
      value: fvProfile.R2.toFixed(3),
      description: 'Model fit quality',
      accent: false,
      status: fvProfile.R2 >= 0.95 ? 'good' : 'warning'
    }
  ];

  // Add new KPIs if available
  if (result.velocityLoss) {
    cards.push({
      title: 'Velocity Loss (%)',
      value: result.velocityLoss.totalVelocityLoss.toFixed(1),
      description: 'Speed lost under max load',
      accent: false
    });
  }

  if (result.powerDistribution) {
    cards.push({
      title: 'Optimal Load (kg)',
      value: result.powerDistribution.optimalLoad.toFixed(1),
      description: 'Load for peak power',
      accent: false
    });
  }

  if (result.trainingReadiness) {
    cards.push({
      title: 'Speed Reserve (m/s)',
      value: result.trainingReadiness.speedReserve.toFixed(1),
      description: 'Speed above race pace',
      accent: false
    });
  }

  if (result.distanceMetrics) {
    cards.push({
      title: 'Sprint Time (s)',
      value: result.distanceMetrics.estimatedSprintTime.toFixed(2),
      description: 'Estimated sprint duration',
      accent: false
    });
  }

  // Add enhanced KPIs if available
  if (result.mechanicalAnalysis) {
    cards.push({
      title: 'Neuromuscular Efficiency (%)',
      value: result.mechanicalAnalysis.neuromuscularEfficiency.toFixed(1),
      description: 'Actual vs theoretical power',
      accent: false
    });
  }

  if (result.fatigueMetrics) {
    cards.push({
      title: 'Fatigue Index (%)',
      value: result.fatigueMetrics.fatigueIndex.toFixed(1),
      description: 'Power decline across trials',
      accent: false
    });
  }

  if (result.validation) {
    cards.push({
      title: 'Measurement Errors',
      value: result.validation.measurementErrors.toString(),
      description: 'Trials with >10% error',
      accent: false,
      status: result.validation.measurementErrors === 0 ? 'good' : 'warning'
    });
  }

  if (result.injuryPrevention) {
    const overallRisk = Math.round((result.injuryPrevention.overreachingRisk + result.injuryPrevention.techniqueBreakdown) / 2);
    cards.push({
      title: 'Injury Risk Score',
      value: overallRisk.toString(),
      description: overallRisk < 25 ? 'Low risk - Continue current training' : 
                   overallRisk < 50 ? 'Moderate risk - Monitor closely' :
                   overallRisk < 75 ? 'High risk - Reduce intensity' :
                   'Critical risk - Rest required',
      accent: false,
      status: overallRisk < 25 ? 'good' : 'warning'
    });
  }

  if (splits) {
    cards.push(
      {
        title: 'MSS (m/s)',
        value: splits.MSS.toFixed(2),
        description: 'Max sprinting speed',
        accent: false
      },
      {
        title: 'MAC (m/s²)',
        value: splits.MAC.toFixed(2),
        description: 'Max acceleration capacity',
        accent: false
      }
    );
  }

  return (
    <div className="space-y-6">
      {/* Classification Badge */}
      <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
        <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-4">Athlete Classification</h3>
        <div className="flex items-center space-x-6">
          <span className={`px-4 py-2 rounded-lg font-semibold ${getBadgeColor(classification.label)}`}>
            {classification.label}
          </span>
          <div className="text-base text-theme-muted tabular-nums">
            <p>Δ = {classification.delta.toFixed(3)}</p>
            <p>zF = {classification.zF.toFixed(3)}, zV = {classification.zV.toFixed(3)}</p>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card, index) => {
          const isAccent = card.accent && index === 0; // Only first card gets accent
          const borderColor = card.status === 'good' ? 'border-l-green-500' : 
                             card.status === 'warning' ? 'border-l-amber-500' : 
                             isAccent ? 'border-l-theme-accent' : 'border-l-slate-600';
          
          return (
            <div key={index} className={`bg-theme-surface-1 border border-theme-border rounded-2xl p-4 shadow-card border-l-4 ${borderColor}`}>
              <h4 className="text-sm font-medium text-theme-muted mb-2">{card.title}</h4>
              <p className="text-2xl font-semibold text-theme-text mb-1 tabular-nums">{card.value}</p>
              <p className="text-sm text-theme-muted">{card.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};